<template>
  <div>
    <div class="modal" id="mou-moa-detail-modal">
      <div class="modal-dialog modal-fullscreen" role="document">
        <div class="modal-content">

          <div class="modal-header">
            <div class="d-flex align-items-center" style="min-width:0; flex:1;">
              <span class="type-badge mr-2" :class="'type-' + doc.document_type">
                {{ (doc.document_type || '').toUpperCase() }}
              </span>
              <h5 class="modal-title font-weight-bold text-truncate mb-0">
                {{ doc.title }}
              </h5>
            </div>
            <div class="d-flex align-items-center ml-3 flex-shrink-0">
              <div v-if="canManage" class="mode-toggle mr-3" role="group">
                <button
                  type="button"
                  class="mode-btn"
                  :class="{ active: mode === 'viewer' }"
                  @click="mode = 'viewer'"
                  title="Viewer mode"
                >
                  <i class="bi bi-eye mr-1"></i> Viewer
                </button>
                <button
                  type="button"
                  class="mode-btn"
                  :class="{ active: mode === 'admin' }"
                  @click="mode = 'admin'"
                  title="Admin mode"
                >
                  <i class="bi bi-pencil-square mr-1"></i> Admin
                </button>
              </div>
              <button type="button" class="close ml-0" data-dismiss="modal" aria-label="Close">
                <span style="font-size:28px;">&times;</span>
              </button>
            </div>
          </div>

          <div class="modal-body p-0" style="overflow:hidden;">
            <div class="row no-gutters" style="height:calc(100vh - 80px);">

              <!-- ── Left panel: details ── -->
              <div class="col-lg-4 col-xl-3 d-flex flex-column detail-panel" style="height:calc(100vh - 80px); min-height:0;">
                <div class="overflow-auto flex-grow-1 px-3 py-3" style="min-height:0;">

                  <table class="table table-borderless table-sm detail-table mb-3">
                    <tbody>
                      <tr>
                        <td class="detail-label">Status</td>
                        <td>
                          <span class="status-badge" :class="'status-' + doc.status">
                            {{ statusLabel(doc.status) }}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td class="detail-label">CHED's Role</td>
                        <td>
                          <span class="role-pill">{{ partyRoleLabel(doc.party_role) }}</span>
                        </td>
                      </tr>
                      <tr>
                        <td class="detail-label">Counterpart</td>
                        <td class="detail-value">{{ doc.counterpart || '—' }}</td>
                      </tr>
                      <tr>
                        <td class="detail-label">Date Signed</td>
                        <td class="detail-value">{{ doc.date_signed | shortDate }}</td>
                      </tr>
                      <tr>
                        <td class="detail-label">Expiry Date</td>
                        <td class="detail-value" :class="{ 'text-danger font-weight-bold': isExpiringSoon }">
                          {{ doc.expiry_date ? (doc.expiry_date | shortDate) : '—' }}
                          <small v-if="isExpiringSoon" class="d-block">Expiring soon</small>
                        </td>
                      </tr>
                      <tr v-if="doc.purpose">
                        <td class="detail-label">Purpose</td>
                        <td class="detail-value">{{ doc.purpose }}</td>
                      </tr>
                      <tr v-if="doc.uploader">
                        <td class="detail-label">Uploaded by</td>
                        <td class="detail-value">{{ uploaderName(doc.uploader) }}</td>
                      </tr>
                    </tbody>
                  </table>

                  <!-- Admin-mode actions -->
                  <template v-if="mode === 'admin' && canManage">
                    <hr class="my-2" />
                    <div class="d-flex flex-column" style="gap:.5rem;">
                      <button class="btn btn-outline-primary btn-sm w-100" @click="$emit('edit', doc)">
                        <i class="bi bi-pencil mr-1"></i> Edit Document
                      </button>
                      <button class="btn btn-outline-danger btn-sm w-100" :disabled="deleting" @click="confirmDelete">
                        <span v-if="deleting" class="spinner-border spinner-border-sm mr-1"></span>
                        <i v-else class="bi bi-trash mr-1"></i> Delete Document
                      </button>
                    </div>
                  </template>

                  <!-- View original link -->
                  <div class="mt-3" v-if="doc.path">
                    <a
                      :href="'https://drive.google.com/file/d/' + doc.path + '/view'"
                      target="_blank"
                      rel="noopener noreferrer"
                      class="small"
                    >
                      <i class="bi bi-box-arrow-up-right mr-1"></i>Open in Google Drive
                    </a>
                  </div>

                </div>
              </div>

              <!-- ── Right panel: PDF viewer ── -->
              <div class="col-lg-8 col-xl-9 d-flex flex-column" style="height:calc(100vh - 80px); border-left:1px solid rgba(0,0,21,.08);">
                <div v-if="!doc.path" class="flex-grow-1 d-flex align-items-center justify-content-center text-muted">
                  <div class="text-center">
                    <i class="bi bi-file-earmark-x" style="font-size:2.5rem; opacity:.35;"></i>
                    <p class="mt-2 mb-0">No PDF uploaded for this document.</p>
                  </div>
                </div>
                <iframe
                  v-else
                  :src="'https://drive.google.com/file/d/' + doc.path + '/preview'"
                  width="100%"
                  style="flex:1; min-height:0; border:none;"
                  allowfullscreen
                ></iframe>
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "MouMoaDetailModal",
  filters: {
    shortDate(value) {
      if (!value) return "—";
      return new Date(value).toLocaleDateString("en-PH", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },
  },
  data() {
    return {
      doc: {
        id: null,
        title: "",
        document_type: "",
        party_role: "",
        counterpart: "",
        purpose: "",
        date_signed: null,
        expiry_date: null,
        status: "",
        path: "",
        uploader: null,
      },
      mode: "viewer",
      deleting: false,
    };
  },
  computed: {
    canManage() {
      const perms = this.$store.state.permissions || [];
      if (perms.includes("mou_moa_manage")) return true;
      const role = this.$store.state.user && this.$store.state.user.role;
      return ["Administrator", "RD", "Chief", "Supervisor", "Records"].includes(role);
    },
    isExpiringSoon() {
      if (!this.doc.expiry_date || this.doc.status !== "active") return false;
      const diff = new Date(this.doc.expiry_date) - new Date();
      return diff > 0 && diff < 90 * 24 * 60 * 60 * 1000;
    },
  },
  methods: {
    show(document) {
      this.doc = { ...document };
      this.mode = "viewer";
      this.deleting = false;
      $("#mou-moa-detail-modal").modal("show");
    },
    statusLabel(status) {
      return { active: "Active", expired: "Expired", terminated: "Terminated" }[status] || status;
    },
    partyRoleLabel(role) {
      return { first_party: "First / Initiating Party", partner: "Partner / Co-signatory" }[role] || role;
    },
    uploaderName(uploader) {
      if (!uploader) return "—";
      return [uploader.first_name, uploader.last_name].filter(Boolean).join(" ");
    },
    confirmDelete() {
      if (!confirm(`Delete "${this.doc.title}"?\n\nThis cannot be undone.`)) return;
      this.deleting = true;
      axios
        .delete(`api/mou-moa/${this.doc.id}`)
        .then((res) => {
          toastr.success((res.data && res.data.message) || "Deleted.", "Information");
          $("#mou-moa-detail-modal").modal("hide");
          this.$emit("deleted");
        })
        .catch(() => {
          toastr.error("Failed to delete document.", "Error");
        })
        .finally(() => {
          this.deleting = false;
        });
    },
  },
  mounted() {
    $("#mou-moa-detail-modal").on("hidden.bs.modal", () => {
      this.doc = {
        id: null, title: "", document_type: "", party_role: "",
        counterpart: "", purpose: "", date_signed: null,
        expiry_date: null, status: "", path: "", uploader: null,
      };
      this.mode = "viewer";
    });
  },
};
</script>

<style scoped>
.modal-fullscreen {
  max-width: 100%;
  margin: 0;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100vh;
  display: flex;
  position: fixed;
  z-index: 100000;
}

.detail-panel {
  background: #fff;
}

.detail-table td {
  padding: .35rem .5rem;
  vertical-align: top;
}
.detail-label {
  font-size: .72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .35px;
  color: #768192;
  white-space: nowrap;
  width: 110px;
}
.detail-value {
  font-size: .88rem;
  color: #2b3a55;
}

/* ── Mode toggle ── */
.mode-toggle {
  display: inline-flex;
  border: 1px solid rgba(0,0,21,.14);
  border-radius: 10px;
  overflow: hidden;
}
.mode-btn {
  background: transparent;
  border: none;
  padding: .3rem .75rem;
  cursor: pointer;
  font-size: .8rem;
  color: #768192;
  transition: background .15s, color .15s;
  line-height: 1.4;
}
.mode-btn.active { background: #4f46e5; color: #fff; }
.mode-btn:not(.active):hover { background: rgba(0,0,21,.05); }

/* ── Badges (duplicated for scoped isolation) ── */
.type-badge {
  display: inline-block;
  padding: .22rem .55rem;
  border-radius: 999px;
  font-size: .68rem;
  font-weight: 800;
  letter-spacing: .4px;
  flex-shrink: 0;
}
.type-badge.type-mou { background: rgba(77,141,255,.12); color: #4d8dff; }
.type-badge.type-moa { background: rgba(43,182,163,.12); color: #1f8f7f; }

.status-badge {
  display: inline-block;
  padding: .18rem .5rem;
  border-radius: 999px;
  font-size: .68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .3px;
  white-space: nowrap;
}
.status-badge.status-active   { background: rgba(43,182,115,.12); color: #1a7a4a; }
.status-badge.status-expired  { background: rgba(224,144,31,.12);  color: #c97812; }
.status-badge.status-terminated { background: rgba(200,60,60,.1);   color: #b03030; }

.role-pill {
  display: inline-block;
  padding: .18rem .48rem;
  border-radius: 999px;
  font-size: .67rem;
  font-weight: 700;
  background: rgba(79,70,229,.09);
  color: #4f46e5;
  white-space: nowrap;
}
</style>
